import * as React from "react";
import { useTranslation } from "react-i18next";
import { ThemeKind } from "src/types";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import useTheme from "@/components/editor/themes/use-theme";
import { ColorLabels } from "@/components/editor/utils/use-color";
import useViewportContext from "@/hooks/use-viewport-context";

import { Notification } from "./notification-component";

type Props = {
  id: string;
  label?: string;
  totalFiltersSelected?: number;
  isToggled?: boolean;
  onClick: () => void;
};

export default function Filter(props: Props) {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const { viewport } = useViewportContext();
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);

  return (
    <FilterButton
      id={props.id}
      onClick={props.onClick}
      $isToggled={props.isToggled}
      data-testid="filter-button"
      className={currentTheme.kind}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3 6C3 5.44772 3.44772 5 4 5H20C20.5523 5 21 5.44772 21 6C21 6.55228 20.5523 7 20 7H4C3.44772 7 3 6.55228 3 6ZM7 12C7 11.4477 7.44772 11 8 11H16C16.5523 11 17 11.4477 17 12C17 12.5523 16.5523 13 16 13H8C7.44772 13 7 12.5523 7 12ZM11 17C10.4477 17 10 17.4477 10 18C10 18.5523 10.4477 19 11 19H13C13.5523 19 14 18.5523 14 18C14 17.4477 13.5523 17 13 17H11Z"
          fill="currentColor"
        />
      </svg>
      {isMobile ? "" : props.label}
      {props.totalFiltersSelected > 0 && (
        <Notification
          aria-label={t("schedule.total-filtered-results", {
            total_filters_selected: props.totalFiltersSelected,
          })}
          role="status"
          data-testid="notification-badge"
          className={currentTheme.kind}
        >
          {props.totalFiltersSelected}
        </Notification>
      )}
    </FilterButton>
  );
}

const FilterButton = withViewportStyle("button")<{
  $isToggled: boolean;
}>`
  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${({ theme, $isToggled }) =>
    $isToggled
      ? theme.colors[ColorLabels.PRIMARY]
      : theme.colors[ColorLabels.SECONDARY]};
  color: ${({ theme, $isToggled }) =>
    $isToggled
      ? theme.colors[ColorLabels.BACKGROUND]
      : theme.colors[ColorLabels.PRIMARY]};
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
  padding: 0.25rem 0.75rem;

  svg {
    margin-right: 0.5rem

    ${(props) => props.$viewport.mobile`
      margin: 0;
    `}
  }

  ${(props) => props.$viewport.mobile`
    background: transparent;
    padding: 0;
  `}

  &.${ThemeKind.CROSBY} {
    border-radius: 12.5rem;
    
    color: ${({ theme, $isToggled }) =>
      $isToggled ? theme.colors[ColorLabels.VISUALS_2] || "#fff" : "inherit"};
  }
  
  &.${ThemeKind.NOLAN} {
    background: ${({ theme, $isToggled }) =>
      $isToggled
        ? theme.colors[ColorLabels.PRIMARY]
        : theme.colors[ColorLabels.BACKGROUND]};
    color: ${({ theme, $isToggled }) =>
      $isToggled
        ? theme.colors[ColorLabels.BACKGROUND]
        : theme.colors[ColorLabels.PRIMARY]};
    
    ${(props) => props.$viewport.mobile`
      background: transparent !important;
      color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    `}
  }

  &.${ThemeKind.DEFAULT} {
    background: ${({ theme, $isToggled }) =>
      $isToggled
        ? theme.colors[ColorLabels.PRIMARY]
        : theme.colors[ColorLabels.SECONDARY]};
    color: ${({ theme, $isToggled }) =>
      $isToggled
        ? theme.colors[ColorLabels.SECONDARY]
        : theme.colors[ColorLabels.PRIMARY]};
  }
`;
